import React from 'react';
import WidgetCard from 'components/Widget/WidgetCard';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';
import Loading from 'components/Loading';
import { useTranslation } from 'react-i18next';
import { ListItem, List, Typography, Link, useTheme } from '@mui/material';
import { WidgetT } from 'business/definitions';
import { useLayout } from 'routes/Dashboard/contexts/LayoutContext';

const LinkText = ({ index, title }: { index: number; title: WidgetT }) => (
    <Typography sx={{ m: 'auto' }}>
        {index + 1}. {title.content?.text}
    </Typography>
);

type Props = {
    editMode: boolean;
    canEdit: boolean;
    menuItems: NestedMenuOptionT[];
    isPdfModeActivated: boolean;
};

const TableOfContentWidget = ({
    editMode,
    menuItems,
    canEdit,
    isPdfModeActivated,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { layout } = useLayout();

    if (layout == undefined) {
        return (
            <WidgetCard editMode={editMode} menuItems={[]} canEdit={false}>
                <div style={{ margin: 'auto' }}>
                    <Loading />
                </div>
            </WidgetCard>
        );
    }

    const titles = layout?.widgets
        .filter((w) => w.type === 'title')
        .sort((a, b) => (a.position.md.y ?? 0) - (b.position.md.y ?? 0));

    if (!titles || titles.length === 0) {
        return (
            <WidgetCard
                editMode={editMode}
                menuItems={menuItems}
                canEdit={canEdit}
            >
                <Typography sx={{ m: 'auto' }} color="text.secondary">
                    {t('widget.tableOfContentEmpty')}
                </Typography>
            </WidgetCard>
        );
    }

    return (
        <WidgetCard
            editMode={editMode}
            menuItems={menuItems}
            canEdit={canEdit}
            sx={{ justifyContent: 'center' }}
        >
            <List sx={{ overflowY: 'auto' }}>
                {titles.map((title, index) => (
                    <ListItem key={title.key} sx={{ justifyContent: 'center' }}>
                        {isPdfModeActivated ? (
                            <LinkText index={index} title={title} />
                        ) : (
                            <Link
                                href={`#widget-${title.key}`}
                                sx={{
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                    color: theme.palette.primary.main,
                                }}
                            >
                                <LinkText index={index} title={title} />
                            </Link>
                        )}
                    </ListItem>
                ))}
            </List>
        </WidgetCard>
    );
};

export default TableOfContentWidget;
